import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { Button, Card, DataRow, Skeleton, SkeletonGroup } from '@/components/ui';
import { OtpForm } from '@/features/auth/OtpForm';
import { useSession } from '@/features/auth/sessionContext';
import { useQuote } from '@/lib/api/quotes';
import { formatPages, formatPaise, formatSheets } from '@/lib/format';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/**
 * /order/details — confirm the customer's phone number.
 *
 * THE UPLOAD SURVIVES THIS STEP. The anonymous `ps_session` cookie set by the
 * upload is what the server uses to reassign the file to the new account when
 * the code is verified, and the `?u=` / `?q=` ids stay in the URL because this
 * step signs in *in place* — no redirect to a login route, no round trip that
 * could drop the query string. The E2E spec asserts exactly that.
 */
export function DetailsStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const session = useSession();
  const quote = useQuote(urlState.quote);

  const goToAddress = () => navigate(stepUrl('address', urlState));
  const authenticated = session.status === 'authenticated' && session.user;

  return (
    <AppShell
      title={step.title}
      subtitle="We need a number for delivery updates."
      progress={stepper}
      backTo={stepUrl('options', urlState)}
      backLabel="Back to print options"
      wide
      action={
        authenticated ? (
          <Button fullWidth onClick={goToAddress}>
            Continue to delivery
          </Button>
        ) : undefined
      }
    >
      {/* Sign-in on the left, what you are about to buy on the right. The
          summary is the reassurance that makes handing over a phone number
          feel reasonable, so on desktop it sits next to the field rather than
          under it. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        {session.status === 'loading' ? (
          <SkeletonGroup label="Checking whether you are signed in">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-touch w-full" />
              <Skeleton className="h-touch w-full" />
            </div>
          </SkeletonGroup>
        ) : authenticated ? (
          <Card title="Signed in">
            <p className="text-base text-ink">
              {session.user?.name ? `${session.user.name} · ` : ''}
              {session.user?.phone}
            </p>
            <p className="mt-1 text-sm text-ink-muted">
              We will text delivery updates to this number.
            </p>
            <div className="mt-3">
              <Button variant="ghost" onClick={() => void session.signOut()}>
                Use a different number
              </Button>
            </div>
          </Card>
        ) : (
          <OtpForm onSignedIn={goToAddress} />
        )}

        {quote.data ? (
          <Card title="Your order so far">
            <dl>
              {/* Per document, because each can have its own copy count. With
                  one document — still most orders — this reads exactly as it
                  always did. */}
              {quote.data.items.map((item, index) => (
                <DataRow
                  key={item.upload_id}
                  label={quote.data.items.length > 1 ? `Document ${index + 1}` : 'Pages'}
                  value={
                    quote.data.items.length > 1
                      ? `${formatPages(item.page_count)}, ${item.copies === 1 ? '1 copy' : `${item.copies} copies`}`
                      : formatPages(item.page_count)
                  }
                />
              ))}
              {quote.data.items.length === 1 && quote.data.items[0] ? (
                <DataRow
                  label="Copies"
                  value={
                    quote.data.items[0].copies === 1
                      ? '1 copy'
                      : `${quote.data.items[0].copies} copies`
                  }
                />
              ) : null}
              <DataRow label="Paper" value={formatSheets(quote.data.sheets)} />
              <DataRow label="Total so far" value={formatPaise(quote.data.total_paise)} emphasis />
            </dl>
            <p className="mt-2 text-sm text-ink-subtle">
              Delivery is added on the next screen, once we know the PIN code.
            </p>
          </Card>
        ) : null}
      </div>
    </AppShell>
  );
}
